import { useEffect } from "react";
import { BASE_URL, OG_IMAGE, type PageMeta } from "../seo/meta";

type SEOProps = PageMeta & {
  type?: "website" | "article";
  ogImage?: string;
};

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

// Updates the document head directly (react-helmet-async does not work with
// React 19). The prerendered HTML already carries the same tags for crawlers
// that do not run JavaScript.
export function SEO({
  title,
  description,
  path,
  type = "website",
  structuredData,
  ogImage = OG_IMAGE,
}: SEOProps) {
  const url = `${BASE_URL}${path}`;
  const structuredJson = structuredData ? JSON.stringify(structuredData) : null;

  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    setCanonical(url);

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:site_name", "Peslar.com");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    document.head
      .querySelectorAll('script[type="application/ld+json"]')
      .forEach((el) => el.remove());
    if (structuredJson) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = structuredJson;
      document.head.appendChild(script);
    }
  }, [title, description, url, type, ogImage, structuredJson]);

  return null;
}
